import { LearningLayout } from "@jambushrushti/components/learning/LearningLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@jambushrushti/components/ui/card";
import { Button } from "@jambushrushti/components/ui/button";
import { Badge } from "@jambushrushti/components/ui/badge";
import { Link } from "react-router-dom";
import {
  BookOpen,
  Users,
  Video,
  Calendar,
  TrendingUp,
  Clock,
  PlayCircle,
  FileText,
  Plus,
  ArrowRight,
  GraduationCap,
  ClipboardList,
  Sparkles,
  Target,
  Award,
} from "lucide-react";
import { useAdminCourses } from "@jambushrushti/hooks/useLMS";
import { useAdminAuth } from "@jambushrushti/contexts/AdminAuthContext";

const LMSDashboard = () => {
  const { data: courses = [] } = useAdminCourses();
  const { user } = useAdminAuth();

  // Calculate stats
  const totalCourses = courses.length;
  const publishedCourses = courses.filter(c => c.is_published).length;
  const onlineCourses = courses.filter(c => c.course_mode === "Online").length;
  const offlineCourses = courses.filter(
    c => c.course_mode === "Offline"
  ).length;

  const stats = [
    {
      label: "Total Courses",
      value: totalCourses,
      icon: BookOpen,
      color: "bg-primary/10 text-primary",
    },
    {
      label: "Published",
      value: publishedCourses,
      icon: TrendingUp,
      color: "bg-emerald-500/10 text-emerald-600",
    },
    {
      label: "Online Courses",
      value: onlineCourses,
      icon: Video,
      color: "bg-blue-500/10 text-blue-600",
    },
    {
      label: "Offline Courses",
      value: offlineCourses,
      icon: Users,
      color: "bg-amber-500/10 text-amber-600",
    },
  ];

  const quickActions = [
    {
      label: "Add New Course",
      href: "/learning/manage-courses",
      icon: Plus,
      description: "Create a new course",
    },
    {
      label: "Schedule Lecture",
      href: "/learning/manage-lectures",
      icon: Calendar,
      description: "Add online or offline lecture",
    },
    {
      label: "Upload Materials",
      href: "/learning/materials",
      icon: FileText,
      description: "Add study resources",
    },
    {
      label: "View Attendance",
      href: "/learning/attendance",
      icon: ClipboardList,
      description: "Track student attendance",
    },
  ];

  return (
    <LearningLayout title="LMS Dashboard">
      <div className="space-y-8">
        {/* Welcome Section */}
        <div className="relative overflow-hidden rounded-3xl border border-primary/10 bg-gradient-to-br from-primary/10 via-primary/5 to-background p-8">
          {/* Decorative Elements */}
          <div className="absolute right-0 top-0 h-72 w-72 -translate-y-1/2 translate-x-1/2 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute bottom-0 left-0 h-56 w-56 -translate-x-1/2 translate-y-1/2 rounded-full bg-primary/5 blur-2xl" />

          <div className="relative z-10">
            <div className="flex flex-col justify-between gap-6 md:flex-row md:items-center">
              <div>
                <div className="mb-3 flex items-center gap-2">
                  <Badge className="border-primary/20 bg-primary/10 font-medium text-primary">
                    <Sparkles className="mr-1 h-3 w-3" />
                    Learning Management
                  </Badge>
                </div>
                <h1 className="font-heading mb-2 text-3xl font-bold text-foreground md:text-4xl">
                  Welcome back, {user?.name?.split(" ")[0] || "Admin"}!
                </h1>
                <p className="max-w-xl text-lg text-muted-foreground">
                  Manage courses, lectures, students, and learning materials
                  from your dashboard.
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  asChild
                  size="lg"
                  className="gap-2 rounded-xl shadow-lg shadow-primary/20"
                >
                  <Link to="/learning/manage-courses">
                    <Plus className="h-5 w-5" />
                    Add Course
                  </Link>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  size="lg"
                  className="gap-2 rounded-xl"
                >
                  <Link to="/learning/courses">
                    <BookOpen className="h-5 w-5" />
                    View All
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          {stats.map(stat => (
            <Card
              key={stat.label}
              className="group rounded-2xl border-border transition-all duration-300 hover:shadow-lg"
            >
              <CardContent className="p-5 sm:p-6">
                <div className="flex items-center gap-4">
                  <div
                    className={`rounded-xl p-3.5 ${stat.color} transition-transform duration-300 group-hover:scale-110`}
                  >
                    <stat.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <p className="text-3xl font-bold text-foreground">
                      {stat.value}
                    </p>
                    <p className="text-sm font-medium text-muted-foreground">
                      {stat.label}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {quickActions.map(action => (
            <Link key={action.label} to={action.href}>
              <Card className="group h-full cursor-pointer overflow-hidden rounded-2xl border-border transition-all duration-300 hover:border-primary/40 hover:shadow-xl">
                <CardContent className="relative flex flex-col items-center p-6 text-center">
                  {/* Background decoration */}
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

                  <div className="relative z-10">
                    <div className="mb-4 rounded-2xl bg-primary/10 p-4 text-primary transition-all duration-300 group-hover:scale-110 group-hover:bg-primary group-hover:text-primary-foreground group-hover:shadow-lg">
                      <action.icon className="h-7 w-7" />
                    </div>
                    <h3 className="text-lg font-semibold text-foreground">
                      {action.label}
                    </h3>
                    <p className="mt-2 text-sm text-muted-foreground">
                      {action.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Recent Courses */}
        <Card className="rounded-2xl border-border">
          <CardHeader className="flex flex-row items-center justify-between pb-4">
            <div>
              <CardTitle className="font-heading flex items-center gap-2 text-xl">
                <BookOpen className="h-5 w-5 text-primary" />
                Recent Courses
              </CardTitle>
              <CardDescription>Your latest courses</CardDescription>
            </div>
            <Button variant="outline" size="sm" asChild className="rounded-xl">
              <Link to="/learning/manage-courses">
                View All
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardHeader>
          <CardContent>
            {courses.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-muted/30 py-12 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-primary/10">
                  <GraduationCap className="h-8 w-8 text-primary/60" />
                </div>
                <h4 className="font-heading mb-2 text-lg font-semibold text-foreground">
                  No courses yet
                </h4>
                <p className="mb-4 text-muted-foreground">
                  Create your first course to get started!
                </p>
                <Button className="rounded-xl" asChild>
                  <Link to="/learning/manage-courses">
                    <Plus className="mr-2 h-4 w-4" />
                    Create Course
                  </Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {courses.slice(0, 5).map(course => (
                  <div
                    key={course.id}
                    className="group flex items-center justify-between rounded-xl border border-transparent bg-muted/30 p-4 transition-all duration-300 hover:border-primary/20 hover:bg-muted/50"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex h-14 w-14 flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-primary/10">
                        {course.thumbnail_url ? (
                          <img
                            src={course.thumbnail_url}
                            alt={course.title}
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          <BookOpen className="h-6 w-6 text-primary" />
                        )}
                      </div>
                      <div>
                        <h4 className="font-medium text-foreground transition-colors group-hover:text-primary">
                          {course.title}
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          {course.subject} • {course.level} •{" "}
                          {course.course_mode}
                        </p>
                      </div>
                    </div>
                    <Badge
                      variant={course.is_published ? "default" : "outline"}
                      className="rounded-lg"
                    >
                      {course.is_published ? "Published" : "Draft"}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Feature Cards */}
        <div className="grid gap-6 md:grid-cols-2">
          <Card className="group overflow-hidden rounded-2xl border-border transition-all duration-300 hover:shadow-xl">
            <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
            <CardHeader className="relative">
              <CardTitle className="flex items-center gap-3">
                <div className="rounded-xl bg-emerald-500/10 p-2.5">
                  <Video className="h-5 w-5 text-emerald-600" />
                </div>
                Online Learning
              </CardTitle>
              <CardDescription>
                Schedule live sessions and upload recorded lectures
              </CardDescription>
            </CardHeader>
            <CardContent className="relative space-y-4">
              <div className="flex items-center gap-3 rounded-xl bg-muted/50 p-3 text-sm">
                <PlayCircle className="h-5 w-5 text-emerald-600" />
                <span className="font-medium">
                  Live Zoom/YouTube integration
                </span>
              </div>
              <div className="flex items-center gap-3 rounded-xl bg-muted/50 p-3 text-sm">
                <Video className="h-5 w-5 text-blue-600" />
                <span className="font-medium">Upload recorded lectures</span>
              </div>
              <div className="flex items-center gap-3 rounded-xl bg-muted/50 p-3 text-sm">
                <Clock className="h-5 w-5 text-primary" />
                <span className="font-medium">Track student progress</span>
              </div>
              <Button
                variant="outline"
                className="mt-2 h-11 w-full rounded-xl"
                asChild
              >
                <Link to="/learning/manage-lectures">
                  Manage Lectures
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>

          <Card className="group overflow-hidden rounded-2xl border-border transition-all duration-300 hover:shadow-xl">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
            <CardHeader className="relative">
              <CardTitle className="flex items-center gap-3">
                <div className="rounded-xl bg-blue-500/10 p-2.5">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                Offline Learning
              </CardTitle>
              <CardDescription>
                Manage in-person classes and track attendance
              </CardDescription>
            </CardHeader>
            <CardContent className="relative space-y-4">
              <div className="flex items-center gap-3 rounded-xl bg-muted/50 p-3 text-sm">
                <Calendar className="h-5 w-5 text-emerald-600" />
                <span className="font-medium">Schedule classroom sessions</span>
              </div>
              <div className="flex items-center gap-3 rounded-xl bg-muted/50 p-3 text-sm">
                <ClipboardList className="h-5 w-5 text-blue-600" />
                <span className="font-medium">Manual attendance tracking</span>
              </div>
              <div className="flex items-center gap-3 rounded-xl bg-muted/50 p-3 text-sm">
                <FileText className="h-5 w-5 text-primary" />
                <span className="font-medium">Upload session recordings</span>
              </div>
              <Button
                variant="outline"
                className="mt-2 h-11 w-full rounded-xl"
                asChild
              >
                <Link to="/learning/attendance">
                  View Attendance
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </LearningLayout>
  );
};

export default LMSDashboard;
